import { AlertTriangle, ArrowRight } from 'lucide-react';
import { useUIStore } from '@/stores/ui-store';
import { useModuleStore } from '@/stores/module-store';

/**
 * Full-page warning overlay shown when the SaaS org the user authorized
 * differs from the one they are currently embedded in.
 *
 * Populated by a non-blocking background check in use-app-init.ts step 6c.
 * Today only fires for Zoho CRM (embedded-app SDK exposes `getOrgInfo()`);
 * the overlay itself is provider-agnostic so future SaaS integrations with
 * the same "authorize org A, open extension in org B" problem can reuse it.
 *
 * The CTA clears both the module-store's `saasAuthenticated` flag and
 * dispatches `saas:reauth-required` — SaaSAuthPrompt listens for that
 * event and takes over from there, re-running the OAuth flow so the
 * credentials stored for this extension match the current org.
 */
export function OrgMismatchOverlay() {
  const orgMismatch = useUIStore((s) => s.orgMismatch);
  const setOrgMismatch = useUIStore((s) => s.setOrgMismatch);
  const saasModule = useModuleStore((s) => s.saasModule);
  const setSaaSAuthenticated = useModuleStore((s) => s.setSaaSAuthenticated);

  if (!orgMismatch) return null;

  const saasName = saasModule?.name || 'your CRM';
  const authorized = orgMismatch.authorizedOrgName
    ? `${orgMismatch.authorizedOrgName} (ID: ${orgMismatch.authorizedOrgId})`
    : `ID ${orgMismatch.authorizedOrgId}`;

  const handleReauthenticate = () => {
    // Flip saasAuthenticated to false so SaaSAuthPrompt renders, then
    // dismiss this overlay. The prompt drives the OAuth re-run.
    setSaaSAuthenticated(false);
    window.dispatchEvent(new Event('saas:reauth-required'));
    setOrgMismatch(null);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-xl border border-border bg-card shadow-lg p-6 space-y-5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500/15">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="min-w-0">
            <h2 className="font-display text-base font-semibold text-foreground">
              Different {saasName} account detected
            </h2>
            <p className="text-xs text-muted-foreground">
              Re-authentication is required to continue.
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-muted/30 px-3 py-3 space-y-2 text-sm">
          <div className="flex items-start gap-2">
            <span className="text-xs uppercase tracking-wide text-muted-foreground shrink-0 w-20 pt-0.5">Authorized</span>
            <span className="text-foreground font-medium break-all">{authorized}</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-xs uppercase tracking-wide text-muted-foreground shrink-0 w-20 pt-0.5">Current</span>
            <span className="text-foreground font-medium break-all">ID {orgMismatch.currentOrgId}</span>
          </div>
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed">
          You authorized Ulgebra in a different {saasName} account. Continuing
          would read and write records in the authorized account, not this
          one. Re-authenticate below to connect Ulgebra to this account
          instead.
        </p>

        <button
          type="button"
          onClick={handleReauthenticate}
          className="w-full flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          Re-authenticate with this account
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
